import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Trash2, MessageCircle, CornerDownRight, X } from "lucide-react";
import useComment from "../../hooks/useComment";

// ✅ Recursive Comment Component
const CommentItem = ({ comment, depth = 0, onReply, onDelete, postId }) => {
  const maxDepth = 6;
  const indentColor = [
    "border-white/10",
    "border-blue-500/30",
    "border-purple-500/30",
    "border-pink-500/30",
    "border-orange-500/30",
    "border-green-500/30",
  ];

  return (
    <div className={`${depth > 0 ? "ml-4 border-l pl-3 " + indentColor[depth % indentColor.length] : ""}`}>
      <div className="flex gap-3 py-3 group">
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center 
          justify-center shrink-0 overflow-hidden">
          {comment.avatar ? (
            <img
              src={comment.avatar}
              alt={comment.author}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-white/60 text-xs font-bold">
              {comment.author?.[0]?.toUpperCase()}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-white font-semibold text-sm">
              @{comment.author}
            </span>
            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white/30 text-xs">
                {new Date(comment.created_at).toLocaleDateString()}
              </span>
              <button
                onClick={() => onDelete(comment.id)}
                className="p-1 hover:text-red-400 text-white/20 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>

          <p className="text-white/80 text-sm mt-0.5 leading-relaxed break-words">
            {comment.content}
          </p>

          {/* Reply Button */}
          {depth < maxDepth && (
            <button
              onClick={() => onReply({ id: comment.id, author: comment.author })}
              className="flex items-center gap-1 mt-1.5 text-xs text-white/30
                hover:text-white/60 transition-colors"
            >
              <CornerDownRight className="w-3 h-3" />
              Reply
            </button>
          )}
        </div>
      </div>

      {/* Nested Replies */}
      {comment.replies?.length > 0 && (
        <div>
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              onReply={onReply}
              onDelete={onDelete}
              postId={postId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ Main Modal
export default function CommentModal({ isOpen, onClose, postId }) {
  const {
    comments, loading, posting, text, replyTo, error,
    setText, setReplyTo, fetchComments, handlePost, handleDelete,
  } = useComment(postId);

  const sheetRef = useRef(null);
  const startY = useRef(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) fetchComments();
  }, [isOpen, fetchComments]);

  // Focus input when replying
  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = (e) => {
    const diff = e.changedTouches[0].clientY - startY.current;
    if (diff > 120) onClose();
    else if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(0px)`;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
    if (e.key === "Escape") setReplyTo(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            ref={sheetRef}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="absolute bottom-0 w-full bg-[#111] rounded-t-3xl
              max-h-[82vh] overflow-hidden border-t border-white/10"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Handle */}
            <div className="w-full flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-white/60" />
              <span className="text-white font-semibold text-base">Comments</span>
              {comments.length > 0 && (
                <span className="text-white/40 text-sm">({comments.length})</span>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="mx-4 mt-3 p-3 bg-red-500/10 border border-red-500/20
                rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Comments List */}
            <div
              className="px-4 overflow-y-auto pb-28 mt-2"
              style={{ maxHeight: "calc(82vh - 160px)" }}
            >
              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-white/30" />
                </div>
              ) : comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <MessageCircle className="w-10 h-10 text-white/10" />
                  <p className="text-white/30 text-sm">
                    No comments yet — be the first!
                  </p>
                </div>
              ) : (
                comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    depth={0}
                    onReply={setReplyTo}
                    onDelete={handleDelete}
                    postId={postId}
                  />
                ))
              )}
            </div>

            {/* Input Bar */}
            <div className="absolute bottom-0 left-0 w-full bg-[#111]
              border-t border-white/10 px-4 py-3">

              {/* Reply indicator */}
              {replyTo && (
                <div className="flex items-center justify-between mb-2
                  bg-white/5 rounded-lg px-3 py-1.5">
                  <span className="text-white/50 text-xs flex items-center gap-1">
                    <CornerDownRight className="w-3 h-3" />
                    Replying to <span className="text-white/80 font-medium ml-1">@{replyTo.author}</span>
                  </span>
                  <button
                    onClick={() => setReplyTo(null)}
                    className="text-white/30 hover:text-white/60"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={replyTo ? `Reply to @${replyTo.author}...` : "Add a comment..."}
                  className="flex-1 bg-white/5 border border-white/10 rounded-full
                    px-4 py-2.5 text-white text-sm outline-none placeholder-white/30
                    focus:border-white/30 transition-colors"
                />
                <button
                  onClick={handlePost}
                  disabled={!text.trim() || posting}
                  className={`p-2.5 rounded-full transition-all duration-200
                    ${!text.trim() || posting
                      ? "bg-white/5 text-white/20 cursor-not-allowed"
                      : "bg-white text-black hover:bg-gray-100 active:scale-95"
                    }`}
                >
                  {posting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}